/**
 * Boom Fantasy `mockresponse` helpers for local/staging debugging.
 *
 * Mocks are OFF by default. A deployed staging HC iframe with only `?env=staging`
 * hits live Boom — no `mockResponse` is sent. Opt in explicitly with `boomMock`:
 * - `?boomMock=1` → per-route scenario defaults below
 * - `?boomMock=<code>` → force that code on every mockable read
 * - `?boomMock=0` / omit → live Boom
 *
 * GraphQL only (`env=dev|staging`). Prod stays on campaigns REST, so this code
 * path never runs there. Experience API also refuses to forward the header
 * outside sandbox / staging.
 *
 * Boom uses different vocabularies per endpoint (`GET /picks` vs contest
 * leaderboards). The defaults below are a coherent "regular season live"
 * pair — do not mark both season and playoffs as `current`.
 *
 * @see Boom doc "CLIENT: Tim Hortons API Manual"
 */

/**
 * Opt-in defaults when `boomMock=1`. Regular season live, playoffs not started.
 *
 * @type {Record<string, string>}
 */
export const MOCK_BY_ROUTE = {
  seasonLeaderboard: 'current-season',
  seasonLeaderboardLocal: 'current-season',
  leaderboard: 'challenge-2-current',
  leaderboardLocal: 'challenge-2-current',
  postSeasonLeaderboard: 'future-postseason',
  postSeasonLeaderboardLocal: 'future-postseason',
  userStats: 'challenge-2-current',

  // GET /picks vocabulary (not contest codes — those return hc.45).
  players: 'not-picked-yet',
  pickHistory: 'not-picked-yet',
};

const OFF_VALUES = new Set(['0', 'false', 'off', 'no']);
const ON_VALUES = new Set(['1', 'true', 'on', 'yes']);

function readBoomMockParam(search) {
  const raw = search ?? (typeof window !== 'undefined' ? window.location.search : undefined);
  if (!raw) {
    return undefined;
  }
  const value = new URLSearchParams(raw).get('boomMock');
  return value == null || value === '' ? undefined : value.trim();
}

/**
 * Resolve the Boom mock code for a GraphQL route.
 *
 * @param {string} route makeRequest route name
 * @param {string} [search] query string, defaults to the current URL
 * @returns {string|undefined} mock code, or undefined to send no mock
 */
export function getMockResponse(route, search) {
  const param = readBoomMockParam(search);
  if (param == null) {
    return undefined;
  }
  if (OFF_VALUES.has(param.toLowerCase())) {
    return undefined;
  }
  if (ON_VALUES.has(param.toLowerCase())) {
    return MOCK_BY_ROUTE[route];
  }
  return param;
}
